import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { useLang } from "@/i18n";
import { ShieldCheck, Play, Star, AlertCircle, Phone } from "lucide-react";
import { meta } from "@/analytics/metaPixel";
import PhoneHours, { trackCall } from "./PhoneHours";

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onCta }) {
  const { t } = useLang();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const stats = [
    [t("hero.s1v"), t("hero.s1l")],
    [t("hero.s2v"), t("hero.s2l")],
    [t("hero.s3v"), t("hero.s3l")],
    [t("hero.s4v"), t("hero.s4l")],
  ];
  const badges = t("hero.badges").split("|");

  const play = () => {
    setPlaying(true);
    videoRef.current?.play();
  };

  return (
    <section ref={ref} id="top" className="hero-glow relative pt-40 pb-16 overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-[6%] w-72 h-72 rounded-full bg-[#00AEEF]/12 blur-[100px]" />
        <div className="absolute bottom-10 left-[2%] w-80 h-80 rounded-full bg-[#003087]/8 blur-[110px]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-[#00AEEF]/30 bg-[#E6F6FD] rounded-full px-4 py-1.5 mb-7"
            data-testid="hero-badge"
          >
            <ShieldCheck size={14} className="text-[#00AEEF]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0077A3]">{t("hero.eyebrow")}</span>
          </motion.div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#003087] leading-[1.12]" data-testid="hero-headline">
            <Line delay={0.15}>{t("hero.title")}</Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-4 text-lg sm:text-xl font-bold text-[#00AEEF]" data-testid="hero-highlight">
            {t("hero.highlight")}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-4 text-base leading-relaxed text-[#1A1A1A]" data-testid="hero-sub">
            {t("hero.sub")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-4">
            <button data-testid="hero-primary-cta-button" onClick={() => onCta("visure")}
              className="btn-green rounded-full px-8 py-4 text-base font-bold">
              {t("hero.cta2")}
            </button>
            <a href="#come-funziona" data-testid="hero-secondary-cta-button"
              className="btn-outline rounded-full px-8 py-4 text-base font-bold text-center">
              {t("hero.cta1")}
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }} className="mt-4">
            <a data-testid="hero-call-link" href="tel:+393713142397"
              onClick={() => trackCall("hero_call")}
              className="inline-flex items-center gap-2.5 group">
              <span className="w-9 h-9 rounded-full bg-[#003087] text-white flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Phone size={18} />
              </span>
              <span>
                <span className="block text-sm font-bold text-[#003087] group-hover:text-[#00AEEF] transition-colors duration-200">{t("hero.wa")}</span>
                <span className="block text-[11px] text-[#64748B]">{t("hero.wa.sub")}</span>
                <PhoneHours className="mt-0.5" />
              </span>
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
            {badges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1A1A1A]">
                <Star size={11} className="text-[#00AEEF]" fill="#00AEEF" /> {b}
              </span>
            ))}
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-5 flex items-start gap-2 text-xs text-[#64748B] max-w-md leading-relaxed" data-testid="hero-urgency">
            <AlertCircle size={14} className="text-[#00AEEF] shrink-0 mt-0.5" />
            {t("hero.urgency")}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
          className="relative" data-testid="hero-video-card"
        >
          <div className="card rounded-3xl overflow-hidden">
            <div className="relative aspect-video bg-[#003087]">
              <video ref={videoRef} src="/video/explainer.mp4" poster="/video/poster.jpg" controls={playing} playsInline preload="metadata"
                className="w-full h-full object-cover" data-testid="hero-explainer-video" />
              {!playing && (
                <button onClick={play} data-testid="hero-video-play-button"
                  className="absolute inset-0 flex items-center justify-center group bg-[#003087]/20 hover:bg-[#003087]/10 transition-colors duration-300" aria-label="play">
                  <span className="pulse-ring w-20 h-20 rounded-full bg-[#00AEEF] text-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Play size={28} fill="currentColor" className="ml-1" />
                  </span>
                </button>
              )}
            </div>
            <div className="p-5">
              <p className="font-bold text-[#003087] text-sm">{t("hero.video.title")}</p>
              <p className="text-xs text-[#64748B] mt-1">{t("hero.video.note")}</p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
        className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 mt-14" data-testid="hero-stats-container">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(([v, l], i) => (
            <div key={i} className="card rounded-2xl px-5 py-5" data-testid={`hero-stat-${i + 1}`}>
              <div className="text-2xl sm:text-3xl font-extrabold text-[#00AEEF]">{v}</div>
              <div className="text-[11px] sm:text-xs text-[#64748B] mt-1 leading-snug font-medium">{l}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] text-[#94A3B8]">{t("hero.demo")}</p>
      </motion.div>
    </section>
  );
}
